/**
 * Anlagetunnel: Kunde → Spielplätze → Geräte.
 *
 * Der Prüftunnel draußen stellt eine Frage je Bildschirm, weil man dort mit
 * Handschuhen und einer Hand arbeitet. Hier ist es umgekehrt: das Anlegen
 * passiert am Schreibtisch mit Tastatur, oft mit einer Liste daneben. Ein
 * Feld je Bildschirm wäre eine Qual. Also eine Stufe je Gegenstand, und
 * innerhalb der Stufe ein zusammenhängendes Formular.
 *
 * Zwei Entscheidungen, die den Unterschied machen:
 *
 * 1. Es wird sofort gespeichert, nicht am Ende. Wer beim siebten Gerät
 *    abbricht, behält die ersten sechs. Ein Assistent, der alles erst zum
 *    Schluss schreibt, verliert bei jedem Fehler eine halbe Stunde.
 *
 * 2. Die Gerätestufe merkt sich, was sich wiederholt: Hersteller, Baujahr
 *    und Fallschutzart bleiben stehen, die ID-Nr. zählt weiter. Auf einem
 *    Platz stammen die Geräte fast immer vom selben Hersteller aus
 *    demselben Jahr — das ist die Stelle, an der die Zeit draufgeht.
 */

import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  useGeraetAnlegen,
  useGeraetetypen,
  useKundeAnlegen,
  useKundenarten,
  useSpielplatzAnlegen,
} from '../api/abfragen'
import type { Kunde, Spielplatz } from '../api/typen'
import { Blatt } from '../bausteine/Blatt'
import { Fehlerhinweis } from '../bausteine/Hinweise'
import { Nachfrage, offeneFelder, type Feldangabe } from './Feld'

/* Was auf einem Prüfbericht später fehlen würde, wenn es hier leer bleibt.
   Nicht alles, was das Formular kennt: die Bemerkung ist wirklich
   freiwillig, die Fallhöhe nicht. */
const ERWARTET_KUNDE: Feldangabe[] = [
  { name: 'strasse', beschriftung: 'Straße', warum: 'steht im Briefkopf jeder Rechnung' },
  { name: 'plz', beschriftung: 'PLZ' },
  { name: 'ort', beschriftung: 'Ort' },
  {
    name: 'ansprechpartner',
    beschriftung: 'Ansprechpartner',
    warum: 'wen Sie bei einer Sperrung anrufen',
  },
  {
    name: 'email',
    beschriftung: 'E-Mail',
    warum: 'ohne sie kein Kundenzugang und keine Erinnerung',
  },
]

const ERWARTET_PLATZ: Feldangabe[] = [
  { name: 'strasse', beschriftung: 'Straße' },
  { name: 'ort', beschriftung: 'Ort' },
  {
    name: 'routenziel',
    beschriftung: 'Routenziel',
    warum: 'die Postadresse führt oft nicht zur Zufahrt',
  },
  {
    name: 'altersgruppe',
    beschriftung: 'Altersgruppe',
    warum: 'gehört nach DIN 18034 auf den Bericht',
  },
]

const ERWARTET_GERAET: Feldangabe[] = [
  { name: 'geraetetyp', beschriftung: 'Gerätetyp', warum: 'bestimmt die anzuwendende Norm' },
  { name: 'hersteller', beschriftung: 'Hersteller', warum: 'für Ersatzteile und Rückfragen' },
  { name: 'baujahr', beschriftung: 'Baujahr' },
  {
    name: 'fallschutzart',
    beschriftung: 'Fallschutzart',
    warum: 'ohne sie lässt sich der Fallschutz nicht beurteilen',
  },
  {
    name: 'fallhoehe_cm',
    beschriftung: 'Fallhöhe',
    warum: 'entscheidet nach DIN EN 1177 über den nötigen Fallschutz',
  },
]

type Stufe = 'kunde' | 'platz' | 'geraet' | 'fertig'

export function Anlagetunnel({ schliessen }: { schliessen: () => void }) {
  const [stufe, setzeStufe] = useState<Stufe>('kunde')
  const [kunde, setzeKunde] = useState<Kunde | null>(null)
  const [plaetze, setzePlaetze] = useState<Spielplatz[]>([])
  const [platz, setzePlatz] = useState<Spielplatz | null>(null)
  const [geraeteAnzahl, setzeGeraeteAnzahl] = useState<Record<string, number>>({})
  const navigiere = useNavigate()

  const schrittzahl =
    stufe === 'kunde' ? 1 : stufe === 'platz' ? 2 : stufe === 'geraet' ? 3 : 3

  return (
    <Blatt
      titel={
        stufe === 'kunde'
          ? 'Neuer Kunde'
          : stufe === 'platz'
            ? `Spielplatz ${plaetze.length + 1}`
            : stufe === 'geraet'
              ? `Geräte: ${platz?.name ?? ''}`
              : 'Fertig'
      }
      untertitel={
        stufe === 'fertig'
          ? undefined
          : `Schritt ${schrittzahl} von 3${kunde ? ` · ${kunde.name}` : ''}`
      }
      schliessen={schliessen}
      breit
    >
      {/* Der Weg als Leiste: man soll sehen, wo man ist und was noch kommt.
          Ohne das fühlt sich ein Assistent an wie ein Trichter ohne Boden. */}
      <ol className="mb-4 flex gap-1 text-base" aria-label="Schritte">
        {(
          [
            ['kunde', 'Kunde'],
            ['platz', 'Spielplätze'],
            ['geraet', 'Geräte'],
          ] as const
        ).map(([wert, text], i) => {
          const erledigt = schrittzahl > i + 1 || stufe === 'fertig'
          const jetzt = stufe === wert
          return (
            <li
              key={wert}
              className={`flex-1 rounded-feld border-2 px-2 py-1 text-center font-semibold ${
                jetzt
                  ? 'border-akzent-500 bg-akzent-grund text-akzent-700'
                  : erledigt
                    ? 'border-stufe-ob bg-stufe-ob-grund text-stufe-ob'
                    : 'border-slate-200 text-slate-500'
              }`}
            >
              {erledigt && !jetzt ? '✓ ' : ''}
              {text}
            </li>
          )
        })}
      </ol>

      {stufe === 'kunde' && (
        <Kundenstufe
          fertig={(neu) => {
            setzeKunde(neu)
            setzeStufe('platz')
          }}
        />
      )}

      {stufe === 'platz' && kunde && (
        <Platzstufe
          kunde={kunde}
          bisher={plaetze}
          fertig={(neu) => {
            setzePlaetze((alt) => [...alt, neu])
            setzePlatz(neu)
            setzeStufe('geraet')
          }}
          ueberspringen={() => setzeStufe('fertig')}
        />
      )}

      {stufe === 'geraet' && platz && (
        <Geraetestufe
          platz={platz}
          anzahl={geraeteAnzahl[platz.id] ?? 0}
          gezaehlt={(n) => setzeGeraeteAnzahl((alt) => ({ ...alt, [platz.id]: n }))}
          weitererPlatz={() => setzeStufe('platz')}
          fertig={() => setzeStufe('fertig')}
        />
      )}

      {stufe === 'fertig' && kunde && (
        <div className="space-y-3">
          <p className="rounded-feld border-2 border-stufe-ob bg-stufe-ob-grund p-3 font-semibold text-stufe-ob">
            {kunde.name} ist angelegt: {plaetze.length}{' '}
            {plaetze.length === 1 ? 'Spielplatz' : 'Spielplätze'},{' '}
            {Object.values(geraeteAnzahl).reduce((a, b) => a + b, 0)} Geräte.
          </p>
          <p className="text-slate-700">
            Alles ist schon gespeichert. Ergänzen und ändern lässt sich später
            jederzeit auf der Kundenseite.
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="knopf-haupt"
              onClick={() => {
                schliessen()
                navigiere(`/kunden/${kunde.id}`)
              }}
            >
              Zum Kunden
            </button>
            <button type="button" className="knopf-still" onClick={schliessen}>
              Schließen
            </button>
          </div>
        </div>
      )}
    </Blatt>
  )
}

/**
 * Die Rückfrage bei unvollständigen Angaben — einmal für alle drei Stufen.
 *
 * Beim ersten Abschicken werden die leeren Felder markiert und die Frage
 * gestellt. „Trotzdem speichern“ geht durch. Wer stattdessen ergänzt,
 * bekommt beim nächsten Versuch nur noch das, was dann noch fehlt.
 */
function useNachfrage(erwartet: Feldangabe[]) {
  const [offen, setzeOffen] = useState<Feldangabe[] | null>(null)

  /** Gibt true zurück, wenn gespeichert werden darf. */
  function darfSpeichern(formular: HTMLFormElement, uebergehen = false): boolean {
    if (uebergehen) return true
    const fehlend = offeneFelder(formular, erwartet)
    if (fehlend.length === 0) {
      setzeOffen(null)
      return true
    }
    setzeOffen(fehlend)
    return false
  }

  return {
    offen,
    zuruecksetzen: () => setzeOffen(null),
    darfSpeichern,
    fehlt: (name: string) => (offen ?? []).some((f) => f.name === name),
  }
}

/* ── Stufe 1: der Kunde ─────────────────────────────────────────────── */

function Kundenstufe({ fertig }: { fertig: (kunde: Kunde) => void }) {
  const anlegen = useKundeAnlegen()
  // Leer heißt: keine Art gewählt. Der Katalog wird geladen.
  const [art, setzeArt] = useState('')
  const arten = useKundenarten()
  const nachfrage = useNachfrage(ERWARTET_KUNDE)
  const formularRef = useRef<HTMLFormElement>(null)

  function speichern(formular: HTMLFormElement, uebergehen = false) {
    if (!nachfrage.darfSpeichern(formular, uebergehen)) return
    const werte = new FormData(formular)
    const daten = Object.fromEntries(
      [...werte.entries()].filter(([, w]) => String(w).trim() !== ''),
    )
    anlegen.mutate(daten as never, { onSuccess: fertig })
  }

  return (
    <form
      ref={formularRef}
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault()
        speichern(e.currentTarget)
      }}
    >
      <div>
        <label htmlFor="t-name">Wem gehören die Plätze?</label>
        <input
          id="t-name"
          name="name"
          required
          autoFocus
          placeholder="Gemeinde Krummhörn"
        />
      </div>

      <div>
        <label htmlFor="t-art">Art</label>
        <select
          id="t-art"
          name="art"
          value={art}
          onChange={(e) => setzeArt(e.target.value)}
        >
          <option value="">— keine Angabe —</option>
          {(arten.data ?? []).map((a) => (
            <option key={a.id} value={a.name}>
              {a.name}
            </option>
          ))}
        </select>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label htmlFor="t-strasse">Straße</label>
          <input
            id="t-strasse"
            name="strasse"
            className={nachfrage.fehlt('strasse') ? 'border-stufe-2 bg-stufe-2-grund' : undefined} placeholder="Rathausstraße 1" />
        </div>
        <div>
          <label htmlFor="t-plz">PLZ</label>
          <input
            id="t-plz"
            name="plz"
            className={nachfrage.fehlt('plz') ? 'border-stufe-2 bg-stufe-2-grund' : undefined} inputMode="numeric" placeholder="26736" />
        </div>
        <div>
          <label htmlFor="t-ort">Ort</label>
          <input
            id="t-ort"
            name="ort"
            className={nachfrage.fehlt('ort') ? 'border-stufe-2 bg-stufe-2-grund' : undefined} placeholder="Krummhörn" />
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor="t-ap">Ansprechpartner</label>
          <input
            id="t-ap"
            name="ansprechpartner"
            className={nachfrage.fehlt('ansprechpartner') ? 'border-stufe-2 bg-stufe-2-grund' : undefined} placeholder="Frau Janssen, Bauhof" />
        </div>
        <div>
          <label htmlFor="t-tel">Telefon</label>
          <input id="t-tel" name="telefon" type="tel" />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="t-mail">E-Mail</label>
          <input
            id="t-mail"
            name="email"
            className={nachfrage.fehlt('email') ? 'border-stufe-2 bg-stufe-2-grund' : undefined} type="email" placeholder="bauhof@krummhoern.de" />
        </div>
      </div>

      <Fehlerhinweis fehler={anlegen.error} />

      {nachfrage.offen && (
        <Nachfrage
          offen={nachfrage.offen}
          laeuft={anlegen.isPending}
          zurueck={nachfrage.zuruecksetzen}
          trotzdem={() => formularRef.current && speichern(formularRef.current, true)}
        />
      )}

      <button type="submit" className="knopf-haupt" disabled={anlegen.isPending}>
        {anlegen.isPending ? 'Legt an …' : 'Speichern und weiter zu den Plätzen'}
      </button>
    </form>
  )
}

/* ── Stufe 2: ein Spielplatz ────────────────────────────────────────── */

function Platzstufe({
  kunde,
  bisher,
  fertig,
  ueberspringen,
}: {
  kunde: Kunde
  bisher: Spielplatz[]
  fertig: (platz: Spielplatz) => void
  ueberspringen: () => void
}) {
  const anlegen = useSpielplatzAnlegen()
  const nachfrage = useNachfrage(ERWARTET_PLATZ)
  const formularRef = useRef<HTMLFormElement>(null)

  function speichern(formular: HTMLFormElement, uebergehen = false) {
    if (!nachfrage.darfSpeichern(formular, uebergehen)) return
    const werte = new FormData(formular)
    const roh = Object.fromEntries(
      [...werte.entries()].filter(([, w]) => String(w).trim() !== ''),
    )
    anlegen.mutate(
      {
        ...roh,
        kunde_id: kunde.id,
        baujahr: roh.baujahr ? Number(roh.baujahr) : undefined,
      } as never,
      { onSuccess: fertig },
    )
  }

  return (
    <form
      ref={formularRef}
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault()
        speichern(e.currentTarget)
      }}
    >
      {bisher.length > 0 && (
        <p className="rounded-feld bg-stufe-ob-grund p-2 text-base text-stufe-ob">
          Angelegt: {bisher.map((p) => p.name).join(', ')}
        </p>
      )}

      <div>
        <label htmlFor="t-pname">Wie heißt der Platz?</label>
        <input
          id="t-pname"
          name="name"
          required
          autoFocus
          placeholder="Spielplatz Am Deich"
        />
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor="t-baujahr">Baujahr</label>
          <input id="t-baujahr" name="baujahr" inputMode="numeric" placeholder="2009" />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="t-pstrasse">Straße</label>
          <input
            id="t-pstrasse"
            name="strasse"
            className={nachfrage.fehlt('strasse') ? 'border-stufe-2 bg-stufe-2-grund' : undefined}
            placeholder="Deichstraße 12"
            defaultValue={kunde.strasse ?? ''}
          />
        </div>
        <div>
          <label htmlFor="t-pplz">PLZ</label>
          <input id="t-pplz" name="plz" inputMode="numeric" defaultValue={kunde.plz ?? ''} />
        </div>
        <div>
          <label htmlFor="t-port">Ort</label>
          <input
            id="t-port"
            name="ort"
            className={nachfrage.fehlt('ort') ? 'border-stufe-2 bg-stufe-2-grund' : undefined}
            defaultValue={kunde.ort ?? ''}
          />
        </div>
      </div>

      <div>
        <label htmlFor="t-route">Routenziel für die Anfahrt</label>
        <input id="t-route" name="routenziel" placeholder="Hinter dem Sportplatz" />
        <p className="mt-1 text-base text-slate-600">
          Die Postadresse führt bei einem Spielplatz oft nicht zur Zufahrt.
          Steht im Bericht und im Kalendereintrag.
        </p>
      </div>

      {/* Der Termin gehört hierher und nicht in eine spätere Maske.
          Ohne ihn bleibt der Platz unter „Fällig“ unsichtbar, und die
          erste Prüfung muss jemandem von selbst einfallen. */}
      <div className="karte-akzent">
        <label htmlFor="t-termin">Wann soll zum ersten Mal geprüft werden?</label>
        <input
          id="t-termin"
          name="erste_pruefung_am"
          type="date"
          defaultValue={new Date().toISOString().slice(0, 10)}
        />
        <div className="mt-3">
          <label htmlFor="t-pruefart">Prüfart</label>
          <select id="t-pruefart" name="erste_pruefart" defaultValue="hauptuntersuchung">
            <option value="hauptuntersuchung">Jährliche Hauptuntersuchung</option>
            <option value="operativ">Operative Kontrolle (vierteljährlich)</option>
            <option value="abnahme">Abnahmeprüfung</option>
          </select>
        </div>
        <p className="mt-2 text-base text-slate-700">
          Damit steht der Platz sofort unter <span className="font-semibold">Fällig</span>,
          und 28 Tage vorher geht eine Erinnerung heraus. Feld leeren, wenn der
          Termin noch offen ist.
        </p>
      </div>

      <div>
        <label htmlFor="t-alter">Altersgruppe</label>
        <input id="t-alter" name="altersgruppe" placeholder="3–12 Jahre" />
      </div>

      <Fehlerhinweis fehler={anlegen.error} />

      {nachfrage.offen && (
        <Nachfrage
          offen={nachfrage.offen}
          laeuft={anlegen.isPending}
          zurueck={nachfrage.zuruecksetzen}
          trotzdem={() => formularRef.current && speichern(formularRef.current, true)}
        />
      )}

      <div className="flex flex-wrap gap-2">
        <button type="submit" className="knopf-haupt" disabled={anlegen.isPending}>
          {anlegen.isPending ? 'Legt an …' : 'Speichern und weiter zu den Geräten'}
        </button>
        <button type="button" className="knopf-still" onClick={ueberspringen}>
          {bisher.length > 0 ? 'Keine weiteren Plätze' : 'Ohne Platz beenden'}
        </button>
      </div>
    </form>
  )
}

/* ── Stufe 3: die Geräte ────────────────────────────────────────────── */

/**
 * Die einzige Stufe, die sich wiederholt — und die einzige, bei der es auf
 * Tempo ankommt. Ein Platz mit zwölf Geräten ist der Normalfall.
 */
function Geraetestufe({
  platz,
  anzahl,
  gezaehlt,
  weitererPlatz,
  fertig,
}: {
  platz: Spielplatz
  anzahl: number
  gezaehlt: (n: number) => void
  weitererPlatz: () => void
  fertig: () => void
}) {
  const anlegen = useGeraetAnlegen(platz.id)
  const typen = useGeraetetypen()
  const [angelegte, setzeAngelegte] = useState<string[]>([])
  const namensfeld = useRef<HTMLInputElement>(null)
  const formularRef = useRef<HTMLFormElement>(null)
  const nachfrage = useNachfrage(ERWARTET_GERAET)
  const [nachWeiter, setzeNachWeiter] = useState(true)

  /* Was sich auf einem Platz wiederholt, bleibt stehen. Die Geräte eines
     Platzes stammen fast immer vom selben Hersteller aus demselben Jahr. */
  const [uebernommen, setzeUebernommen] = useState({
    hersteller: '',
    baujahr: '',
    fallschutzart: '',
  })

  useEffect(() => {
    namensfeld.current?.focus()
  }, [angelegte.length])

  /* Nimmt das Formular selbst statt eines Ereignisses: „Speichern und
     fertig“ ist kein Absenden, und ein nachgebautes Ereignisobjekt wäre
     genau die Art Krücke, die beim nächsten Umbau bricht. */
  function speichern(formular: HTMLFormElement, weiter: boolean, uebergehen = false) {
    if (!nachfrage.darfSpeichern(formular, uebergehen)) {
      // Merken, ob es nach der Rückfrage weitergehen oder enden soll –
      // sonst landet man nach „Trotzdem speichern“ im falschen Zweig.
      setzeNachWeiter(weiter)
      return
    }
    nachfrage.zuruecksetzen()
    const werte = new FormData(formular)
    const roh = Object.fromEntries(
      [...werte.entries()].filter(([, w]) => String(w).trim() !== ''),
    )

    anlegen.mutate(
      {
        ...roh,
        baujahr: roh.baujahr ? Number(roh.baujahr) : undefined,
        fallhoehe_cm: roh.fallhoehe_cm ? Number(roh.fallhoehe_cm) : undefined,
        sortierung: (anzahl + 1) * 10,
      } as never,
      {
        onSuccess: (neu) => {
          setzeAngelegte((alt) => [...alt, neu.bezeichnung])
          gezaehlt(anzahl + 1)
          setzeUebernommen({
            hersteller: String(roh.hersteller ?? ''),
            baujahr: String(roh.baujahr ?? ''),
            fallschutzart: String(roh.fallschutzart ?? ''),
          })
          formular.reset()
          if (!weiter) fertig()
        },
      },
    )
  }

  return (
    <div className="space-y-3">
      {angelegte.length > 0 && (
        <p className="rounded-feld bg-stufe-ob-grund p-2 text-base text-stufe-ob">
          {angelegte.length} {angelegte.length === 1 ? 'Gerät' : 'Geräte'} auf diesem
          Platz: {angelegte.join(', ')}
        </p>
      )}

      <form
        ref={formularRef}
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault()
          speichern(e.currentTarget, true)
        }}
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            {/* Hieß „Bezeichnung am Platz". Das liest sich wie ein
                Standort — gemeint ist der Name des Geräts, unter dem es im
                Protokoll steht. „am Platz" bleibt bei der Nummer, denn dort
                trägt es Bedeutung: es ist die Nummer, die am Gerät klebt,
                und nicht eine, die die Anwendung vergibt. */}
            <label htmlFor="t-gname">Gerätebezeichnung</label>
            <input
              ref={namensfeld}
              id="t-gname"
              name="bezeichnung"
              required
              placeholder="Doppelschaukel West"
            />
          </div>

          <div>
            <label htmlFor="t-gtyp">Gerätetyp</label>
            <select
              id="t-gtyp"
              name="geraetetyp"
              className={nachfrage.fehlt('geraetetyp') ? 'border-stufe-2 bg-stufe-2-grund' : undefined}
              defaultValue=""
            >
              <option value="">— wählen —</option>
              {(typen.data ?? []).map((t) => (
                <option key={t.id} value={t.name}>
                  {t.name}
                  {t.normteil ? ` · ${t.normteil}` : ''}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="t-ghersteller">Hersteller</label>
            <input
              id="t-ghersteller"
              name="hersteller"
              className={nachfrage.fehlt('hersteller') ? 'border-stufe-2 bg-stufe-2-grund' : undefined}
              key={`h-${angelegte.length}`}
              defaultValue={uebernommen.hersteller}
              placeholder="Eibe"
            />
          </div>

          <div>
            <label htmlFor="t-gbaujahr">Baujahr</label>
            <input
              id="t-gbaujahr"
              name="baujahr"
              className={nachfrage.fehlt('baujahr') ? 'border-stufe-2 bg-stufe-2-grund' : undefined}
              inputMode="numeric"
              key={`b-${angelegte.length}`}
              defaultValue={uebernommen.baujahr}
            />
          </div>

          <div>
            <label htmlFor="t-gfs">Fallschutzart</label>
            <input
              id="t-gfs"
              name="fallschutzart"
              className={nachfrage.fehlt('fallschutzart') ? 'border-stufe-2 bg-stufe-2-grund' : undefined}
              key={`f-${angelegte.length}`}
              defaultValue={uebernommen.fallschutzart}
              placeholder="Fallschutzkies"
            />
          </div>

          <div>
            <label htmlFor="t-gfh">Fallhöhe in cm</label>
            <input
              id="t-gfh"
              name="fallhoehe_cm"
              inputMode="numeric"
              className={nachfrage.fehlt('fallhoehe_cm') ? 'border-stufe-2 bg-stufe-2-grund' : undefined}
              placeholder="180"
            />
          </div>
        </div>

        <p className="text-base text-slate-600">
          Hersteller, Baujahr und Fallschutzart bleiben für das nächste Gerät
          stehen, die ID-Nr. zählt weiter.
        </p>

        <Fehlerhinweis fehler={anlegen.error} />

        {nachfrage.offen && (
          <Nachfrage
            offen={nachfrage.offen}
            laeuft={anlegen.isPending}
            zurueck={nachfrage.zuruecksetzen}
            trotzdem={() =>
              formularRef.current && speichern(formularRef.current, nachWeiter, true)
            }
          />
        )}

        <div className="flex flex-wrap gap-2">
          <button type="submit" className="knopf-haupt" disabled={anlegen.isPending}>
            {anlegen.isPending ? 'Legt an …' : 'Speichern und nächstes Gerät'}
          </button>
          <button
            type="button"
            className="knopf-zweit"
            disabled={anlegen.isPending}
            onClick={(e) => {
              const formular = e.currentTarget.form
              // Erst prüfen lassen, sonst ginge ein Gerät ohne Bezeichnung
              // durch – bei „Speichern und fertig“ merkt es niemand.
              if (formular?.reportValidity()) speichern(formular, false)
            }}
          >
            Speichern und fertig
          </button>
        </div>
      </form>

      <div className="flex flex-wrap gap-2 border-t-2 border-slate-200 pt-3">
        <button type="button" className="knopf-still" onClick={weitererPlatz}>
          Weiterer Spielplatz
        </button>
        <button type="button" className="knopf-still" onClick={fertig}>
          Keine weiteren Geräte
        </button>
      </div>
    </div>
  )
}
